import * as React from 'react';
import { t } from 'i18next';

import { BaseComponent } from './BaseComponent';
import { CoreTooltip } from './CoreTooltip';
import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { EMAIL_PATTERN } from '../constants/TextConstants';
import { SelectBoxActions } from '../actions/SelectBoxActions';
import { UIHelper } from '../utils/UIHelper';
import { ClickButton } from './ClickButton';

const enum InputBoxType {
    STD = 0,
    ABORT = 1,
    OK = 2,
    OKabort = 3
}
interface InputBoxState {
    valid: boolean;
    active: boolean;
}

type InputCallback = ((value: string | null) => void) | ((value: number | null) => void);

interface InputBoxProps {
    className: string;
    defaultValue: string | number | null;
    inputType?: 'text' | 'number' | 'email' | 'length';
    placeholder?: string;
    submitCallback?: InputCallback;
    changeCallback?: InputCallback;
    onBlur?: () =>  void;
    onFocus?: () =>  void;
    focus?: boolean;
    type?: InputBoxType;
    noX?: boolean;
    doubleBorder?: boolean;
    noValidIndication?: boolean;
    noTextHelper?: boolean;
    position?: string;
    lines?: number;
    name?: string;
    infoLabel?: string;
}

export class InputBox extends BaseComponent<InputBoxProps, InputBoxState> {
    public input: HTMLInputElement | HTMLTextAreaElement;
    public value: string | number | null;
    public invalid: boolean = false;
    protected tooltip: CoreTooltip;
    protected default: string | number | null;
    protected resetValue: boolean;

    public updateTooltipPosition = (): void => {
        if (this.tooltip != null) {
            this.tooltip.updatePosition();
        }
    };

    public selectAll = (): void => {
        if (
            this.input != null &&
            (
                this.props.inputType == null ||
                this.props.inputType === 'text' ||
                this.props.inputType === 'email'
            )
        ) {
            this.input.setSelectionRange(0, this.input.value.length);
        }
    };
    public focus = (): void  => {
        if (this.input != null) {
            if (AppConfigurationStore.getState().touchDevice) {
                // if we use timeout here the input-box gets focus but keyboard is not shown
                this.input.focus();
            } else {
                // without timeout focus didnt work in FF
                window.setTimeout(() => {this.input.focus(); }, 25);
            }
        }
    };

    public componentWillMount(): void {
        this.value = this.props.defaultValue;
        this.setState({
            valid: true,
            active: false
        });
    }

    public componentDidUpdate(): void {
        if (this.resetValue) {
            this.resetValue = false;
            if (this.input) {
                const newValue: string | null = this.props.defaultValue == null ? null : this.props.defaultValue.toString();
                this.input.value = newValue == null ? '' : newValue;
            }
        }
    }

    public componentDidMount(): void {
        if (this.props.focus && this.input) {
            this.input.focus();
            // FixMe: defaultValue is set too late by react so i cant set selection instantly
            window.setTimeout(
                () => {
                    if (this.props.inputType !== 'number') {
                        this.input.setSelectionRange(0, this.input.value.length);
                    }
                },
                100
            );
        }
    }

    public render(): JSX.Element {
        if (
            this.default !== this.props.defaultValue
        ) {
            this.default = this.props.defaultValue;
            this.value = this.props.defaultValue;
            this.resetValue = true;
        }

        let abortButton: JSX.Element | null = null;
        if (
            this.props.type === InputBoxType.ABORT ||
            this.props.type === InputBoxType.OKabort
        ) {
            abortButton = <ClickButton className="cancel-button" callback={this.abort}>{t('Cancel')}</ClickButton>;
        }
        let submitButton: JSX.Element | null = null;
        if (
            this.props.type === InputBoxType.OK ||
            this.props.type === InputBoxType.OKabort
        ) {
            submitButton = <ClickButton className="submit-button" callback={this.submit}>{t('OK')}</ClickButton>;
        }
        let placeholderDiv: JSX.Element | null = null;

        if (this.props.placeholder && !this.value && this.state.valid) {
            placeholderDiv = <div className="input-placeholder">{this.props.placeholder}</div>;
        }
        // tslint:disable-next-line:no-any
        const inputProps: any = {
            className: ((this.state.valid || this.props.noValidIndication) ? '' : 'invalid') + (this.props.doubleBorder ? ' double-border' : ''),
            type: (this.props.inputType && this.props.inputType !== 'length') ? this.props.inputType : 'text',
            defaultValue: this.props.defaultValue == null ? null : this.props.defaultValue.toString(),
            onChange: this.onChange,
            onFocus: this.onFocus,
            onBlur: this.onBlur,
            required: this.props.inputType === 'email' ? true : false,
            autoFocus: this.props.focus,
            ref: (node: HTMLInputElement | HTMLTextAreaElement) => { this.input = node; },
            autoComplete: this.props.noTextHelper ? 'off' : 'on',
            autoCorrect: this.props.noTextHelper ? 'off' : 'on',
            autoCapitalize: this.props.noTextHelper ? 'off' : 'on',
            spellCheck: !this.props.noTextHelper,
        };

        if (this.props.name != null) {
            inputProps.name = this.props.name;
        }

        if (!this.props.lines || this.props.lines === 1) {
            inputProps.onKeyDown = this.onKeyDown;
        }

        return (
            <div
                className={'input-container' + (this.value ? '' : ' empty-input') + (this.props.position ? ' ' + this.props.position : '')}
                onClick={UIHelper.stopEvent}
            >
                <div className={this.getCSSClass()}>
                    {placeholderDiv}
                    {(this.props.lines && this.props.lines > 1) ? <textarea {...inputProps} rows={this.props.lines}/> : <input {...inputProps}/>}
                    {this.props.noX ? null : <div className="input-delete-button" onClick={this.clearInput}/>}
                </div>
                {abortButton}
                {submitButton}
                {this.props.infoLabel != null ?
                    <CoreTooltip config={{forceLabel: this.props.infoLabel, label: this.props.infoLabel, position: 'top'}} ref={this.setTooltip}/> :
                    null
                }
            </div>
        );
    }

    protected setTooltip = (node: CoreTooltip) => {
        this.tooltip = node;
    };

    protected onChange = (pEvent: React.ChangeEvent): void => {
        const inputElement: HTMLInputElement = pEvent.target as HTMLInputElement;
        let valid: boolean = true;
        if (inputElement.validity && inputElement.validity.badInput) {
            valid = false;
        }

        this.value = valid ? inputElement.value : '';

        if (this.value && this.props.inputType === 'email' && !EMAIL_PATTERN.test(this.value)) {
            valid = false;
        }
        this.invalid = !valid;
        this.setState({
            valid: valid,
            active: this.state.active
        });

        if (this.props.changeCallback != null) {
            // tslint:disable-next-line
            (this.props.changeCallback as any)(this.value);
        }
    };

    protected getCSSClass = (): string => {
        const tResultClasses: Array<string> = ['egr-inputbox'];
        if (this.props.className) {
            tResultClasses.push(this.props.className);
        }
        if (this.state.active) {
            tResultClasses.push('active');
        }
        if (this.value != null && this.value !== '') {
            tResultClasses.push('changed');
        }
        return tResultClasses.join(' ');
    };

    protected onBlur = (pEvent: React.FocusEvent): void => {
        this.setState({
            valid: !this.invalid,
            active: false
        });
        if (this.props.onBlur) {
            this.props.onBlur();
        }
    };

    protected onFocus = (pEvent: React.FocusEvent): void => {
        // Workaround for nested `dispatch` calls:
        setTimeout(
            (): void => {
                SelectBoxActions.setActiveSelectBox(null);
            },
            0
        );

        this.setState({
            valid: !this.invalid,
            active: true
        });
        if (this.props.onFocus) {
            this.props.onFocus();
        }
    };

    protected clearInput = (pEvent: React.MouseEvent): void => {
        UIHelper.stopEvent(pEvent);
        if (this.input) {
            this.value = '';
            this.input.value = '';
            this.input.focus();
            if (this.props.changeCallback) {
                // tslint:disable-next-line
                (this.props.changeCallback as any)(this.value);
            }
        }
    };

    protected onKeyDown = (pEvent: React.KeyboardEvent): void => {
        if (
            (pEvent.keyCode === 13 || pEvent.which === 13) ||
            (
                // The virtual keyboard on android has an Tab-Button instead of Return for number-input
                AppConfigurationStore.getState().os === 'AndroidOS' &&
                this.props.inputType === 'number' &&
                (pEvent.keyCode === 9 || pEvent.which === 9)
            )
        ) {
            if (pEvent.keyCode === 9 || pEvent.which === 9) {
                // needed because otherwise focus would switch to url-input in browser
                UIHelper.stopEvent(pEvent);
            }
            this.submit();
        }
    };

    protected submit = (): void => {
        if (this.input) {
            UIHelper.blurKeyboard();
        }
        if (this.props.submitCallback) {
            // tslint:disable-next-line
            (this.props.submitCallback as any)(this.value ? this.value : this.props.defaultValue);
        }
    };

    protected abort = (): void => {
        if (this.props.submitCallback) {
            // tslint:disable-next-line
            (this.props.submitCallback as any)('');
        }
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/InputBox.tsx